import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const IPDisclosure = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [checkingAgreement, setCheckingAgreement] = useState(true);

  // Check if user has already agreed
  useEffect(() => {
    const checkAgreement = async () => {
      if (user?.role === 'president') {
        try {
          const response = await api.get('/auth/ip-disclosure-status');
          if (response.data.has_agreed) {
            setHasAgreed(true);
          }
        } catch (error) {
          // Endpoint might not exist yet, continue showing disclosure
          console.log('IP disclosure status check:', error);
        }
      }
      setCheckingAgreement(false);
    };
    checkAgreement();
  }, [user, api]);

  const handleAgree = async () => {
    if (!agreed) {
      toast.error('Please read and check the agreement box');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/ip-disclosure-agree', {
        agreed_at: new Date().toISOString(),
        user_id: user?.id,
        user_name: user?.name,
        user_email: user?.email
      });
      toast.success('Agreement recorded. Welcome to the portal.');
      setHasAgreed(true);
      navigate('/portal');
    } catch (error) {
      console.error('Error recording agreement:', error);
      // Still allow access even if recording fails
      toast.success('Welcome to the portal.');
      setHasAgreed(true);
      navigate('/portal');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAgreement) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If user has already agreed or is not the president, redirect
  if (hasAgreed || user?.role !== 'president') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="ip-disclosure-page">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#C6A87C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#C6A87C]" />
          </div>
          <h1 className="font-heading text-2xl mb-2">Intellectual Property Disclosure</h1>
          <p className="text-gray-500">Please review and acknowledge before proceeding</p>
        </div>

        {/* Disclosure Content */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 max-h-[400px] overflow-y-auto">
          <h2 className="font-semibold text-lg mb-4">PROPRIETARY SYSTEM DISCLOSURE AND ACKNOWLEDGMENT</h2>
          
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              <strong>IMPORTANT NOTICE:</strong> This project management system, including all software, designs, 
              features, processes, and intellectual property embodied herein (collectively, the "System"), 
              is the exclusive property of <strong>Randolph Island Jr.</strong> ("Owner") and 
              <strong> RJ Island Realty, LLC</strong> ("Corporate Entity").
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-800">Intellectual Property Rights</p>
                  <p className="text-yellow-700 mt-1">
                    The System is protected by copyright, trade secret, and other intellectual property laws. 
                    All rights are reserved.
                  </p>
                </div>
              </div>
            </div>

            <p>
              <strong>License Grant:</strong> Jax Remodeling Solutions LLC is granted a limited, non-exclusive, 
              non-transferable license to use the System solely for the internal business operations of 
              Jax Remodeling Solutions LLC.
            </p>

            <p>
              <strong>Restrictions:</strong> You agree that you will NOT:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Copy, modify, or create derivative works of the System</li>
              <li>Reverse engineer, disassemble, or decompile any part of the System</li>
              <li>Sell, license, sublicense, or transfer the System to any third party</li>
              <li>Remove or alter any proprietary notices or labels</li>
              <li>Use the System for any purpose other than authorized business operations</li>
              <li>Claim any ownership interest in the System or its intellectual property</li>
              <li>Disclose confidential information about the System to unauthorized parties</li>
            </ul>

            <p>
              <strong>Confidentiality:</strong> The System contains proprietary and confidential information. 
              You agree to maintain strict confidentiality and implement reasonable security measures to 
              protect the System from unauthorized access or disclosure.
            </p>

            <p>
              <strong>No Ownership Rights:</strong> Your use of this System does not grant you any ownership 
              rights or equity interest in the System, its intellectual property, or RJ Island Realty, LLC.
            </p>

            <p>
              <strong>Termination:</strong> The Owner reserves the right to terminate or modify access to 
              the System at any time, with or without cause.
            </p>

            <p>
              <strong>Remedies:</strong> Unauthorized use, copying, or distribution of the System may result 
              in civil and criminal penalties. The Owner reserves all rights to seek injunctive relief and 
              damages for any breach of this agreement.
            </p>

            <p>
              <strong>Governing Law:</strong> This agreement shall be governed by the laws of the State of Florida.
            </p>

            <div className="border-t border-gray-300 pt-4 mt-4">
              <p className="font-semibold">
                By checking the box below and clicking "I Agree," you acknowledge that:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>You have read and understood this disclosure in its entirety</li>
                <li>You agree to comply with all terms and restrictions</li>
                <li>You acknowledge the System is proprietary to RJ Island Realty, LLC</li>
                <li>You understand that violation may result in legal action</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Agreement Checkbox */}
        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="w-5 h-5 mt-0.5 text-[#C6A87C] rounded border-gray-300 focus:ring-[#C6A87C]"
            data-testid="ip-disclosure-checkbox"
          />
          <span className="text-sm text-gray-700">
            I, <strong>{user?.name || 'User'}</strong>, have read, understood, and agree to the 
            Intellectual Property Disclosure and Acknowledgment above. I acknowledge that the System 
            is proprietary to RJ Island Realty, LLC and I will comply with all stated terms and restrictions.
          </span>
        </label>

        {/* Action Button */}
        <button
          onClick={handleAgree}
          disabled={!agreed || loading}
          className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            agreed
              ? 'bg-[#C6A87C] text-white hover:bg-[#B09265]'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          data-testid="ip-disclosure-agree-btn"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              I Agree and Accept
            </>
          )}
        </button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Your agreement will be recorded with timestamp for legal purposes.
        </p>
      </div>
    </div>
  );
};

export default IPDisclosure;
